import dayjs from "dayjs";

import type {
  ClickAction,
  ClickActionsMode,
  ClickObject,
} from "metabase/visualizations/types";
import * as LibMetric from "metabase-lib/metric";
import type { CardId, DatetimeUnit, TemporalUnit } from "metabase-types/api";

import type {
  MetricSourceId,
  MetricsViewerDefinitionEntry,
  MetricsViewerTabState,
} from "../types/viewer-state";

import type { DimensionFilterValue } from "./dimension-filters";
import { findDimensionById } from "./dimension-lookup";

type MetricsViewerClickActionParams = {
  definitions: MetricsViewerDefinitionEntry[];
  tab: MetricsViewerTabState;
  onTabUpdate: (updates: Partial<MetricsViewerTabState>) => void;
  cardIdToDefinitionId: Record<CardId, MetricSourceId>;
};

export class MetricsViewerClickActionsMode implements ClickActionsMode {
  private definitions: MetricsViewerDefinitionEntry[];
  private tab: MetricsViewerTabState;
  private onTabUpdate: (updates: Partial<MetricsViewerTabState>) => void;
  private cardIdToDefinitionId: Record<CardId, MetricSourceId>;
  constructor({
    definitions,
    tab,
    onTabUpdate,
    cardIdToDefinitionId,
  }: MetricsViewerClickActionParams) {
    this.definitions = definitions;
    this.tab = tab;
    this.onTabUpdate = onTabUpdate;
    this.cardIdToDefinitionId = cardIdToDefinitionId;
  }
  actionsForClick(clickObject: ClickObject): ClickAction[] {
    const cardId = clickObject.cardId;
    if (cardId == null) {
      return [];
    }
    const definition = this.definitions.find(
      (definition) => definition.id === this.cardIdToDefinitionId[cardId],
    );
    const params = {
      definitions: this.definitions,
      definition,
      tab: this.tab,
      onTabUpdate: this.onTabUpdate,
      clickObject,
    };
    return [getZoomInTimeSeriesAction(params)].filter(
      (action) => action !== undefined,
    );
  }
}

type GetActionParams = {
  definitions: MetricsViewerDefinitionEntry[];
  definition: MetricsViewerDefinitionEntry | undefined; //definition that was clicked on
  tab: MetricsViewerTabState;
  onTabUpdate: (updates: Partial<MetricsViewerTabState>) => void;
  clickObject: ClickObject;
};

function getZoomInTimeSeriesAction({
  definition,
  tab,
  onTabUpdate,
  clickObject,
}: GetActionParams): ClickAction | undefined {
  if (!definition || !definition.definition) {
    return;
  }
  const dimension = clickObject.dimensions?.[0];
  if (!dimension) {
    return;
  }
  const currentTemporalUnit = dimension.column.unit;
  if (!isValidTemporalUnit(currentTemporalUnit)) {
    return;
  }
  const nextTemporalUnit = getNextTemporalUnit(
    definition,
    tab,
    currentTemporalUnit,
  );
  if (!nextTemporalUnit) {
    return;
  }
  const dimensionValue = dimension.value;
  if (typeof dimensionValue !== "string") {
    return;
  }
  const dimensionFilter = getDimensionFilterForDateAndUnit(
    dimensionValue,
    currentTemporalUnit,
    nextTemporalUnit,
  );
  if (!dimensionFilter) {
    return;
  }
  return {
    type: "custom",
    name: "zoom-in.timeseries",
    title: `See this ${currentTemporalUnit} by ${nextTemporalUnit}`,
    section: "zoom",
    icon: "zoom_in",
    buttonType: "horizontal",
    onClick: ({ closePopover }) => {
      onTabUpdate({
        projectionConfig: {
          ...tab.projectionConfig,
          temporalUnit: nextTemporalUnit,
          dimensionFilter,
        },
      });
      closePopover();
    },
  };
}

function isValidTemporalUnit(unit?: DatetimeUnit): unit is TemporalUnit {
  if (unit == null) {
    return false;
  }
  return ["year", "quarter", "month", "week", "day", "hour"].includes(unit);
}

function getDimensionFilterForDateAndUnit(
  date: string,
  unit: TemporalUnit,
  nextUnit: TemporalUnit,
): DimensionFilterValue | undefined {
  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return undefined;
  }
  return {
    type: "specific-date",
    operator: "between",
    values: [
      parsedDate.startOf(unit).toDate(),
      parsedDate.endOf(unit).toDate(),
    ],
    hasTime: nextUnit === "hour" || nextUnit === "minute",
  };
}

const nextTemporalUnitMap: Partial<Record<TemporalUnit, TemporalUnit>> = {
  year: "quarter",
  quarter: "month",
  month: "week",
  week: "day",
  day: "hour",
  hour: "minute",
};

function getNextTemporalUnit(
  entry: MetricsViewerDefinitionEntry,
  tab: MetricsViewerTabState,
  currentUnit: TemporalUnit,
): TemporalUnit | undefined {
  const definition = entry.definition;
  const dimensionId = tab.dimensionMapping[entry.id];
  if (!definition || !dimensionId) {
    return undefined;
  }
  const dimension = findDimensionById(definition, dimensionId);
  if (!dimension) {
    return undefined;
  }
  const availableBuckets = LibMetric.availableTemporalBuckets(
    definition,
    dimension,
  ).map((bucket) => LibMetric.displayInfo(definition, bucket).shortName);
  const filteredNextTemporalUnitMap = Object.fromEntries(
    Object.entries(nextTemporalUnitMap).filter(([_key, value]) =>
      availableBuckets.includes(value),
    ),
  );
  return filteredNextTemporalUnitMap[currentUnit];
}
